import { Button, CopyableLink, Input } from "@mixitone/components";
import WarnIcon from "@mixitone/icons/warn.svg";
import { EMAIL_ADDRESS_PATTERN, isValidEmailAddress } from "@mixitone/util";
import type { KeyboardEvent } from "react";

interface PartnerInvitePanelProps {
  partnerLink: string;
  partnerEmail: string;
  partnerInviteSent: boolean;
  sendingPartnerInvite: boolean;
  onPartnerEmailChange: (email: string) => void;
  onSendInvite: () => void;
}

export const PartnerInvitePanel = ({
  partnerLink,
  partnerEmail,
  partnerInviteSent,
  sendingPartnerInvite,
  onPartnerEmailChange,
  onSendInvite,
}: PartnerInvitePanelProps) => {
  const emailIsValid = isValidEmailAddress(partnerEmail);
  const emailError =
    partnerEmail.trim().length > 0 && !emailIsValid ? "Enter a valid email address" : undefined;

  const trySendInvite = () => {
    if (partnerInviteSent || sendingPartnerInvite || !emailIsValid) return;
    onSendInvite();
  };

  const handleEmailKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== "Enter") return;
    event.preventDefault();
    trySendInvite();
  };

  return (
    <>
      <div className="rounded-md border border-green-300 bg-white p-4 relative" data-testid="partner-invite">
        <h3 className="font-medium">Invite your doubles partner</h3>
        <p className="mt-1 text-sm">Share this link with your doubles partner:</p>
        <CopyableLink url={partnerLink} />
        <div className="-mb-4 text-center">
          <div className="border-t border-green-300 w-full mt-6" />
          <div className="my-2 px-2 inline-block text-sm text-gray-500 bg-white -translate-y-full">OR</div>
        </div>
        <div className="flex items-start gap-2">
          <Input
            placeholder="Enter your partner's email"
            name="partnerEmail"
            type="email"
            autoComplete="email"
            required
            pattern={EMAIL_ADDRESS_PATTERN}
            title="Enter a valid email address"
            showValid
            error={emailError}
            containerClassName="flex-1"
            className="m-0"
            onChange={(e) => onPartnerEmailChange(e.target.value)}
            onKeyDown={handleEmailKeyDown}
            value={partnerEmail}
            disabled={partnerInviteSent || sendingPartnerInvite}
          />
          <Button
            type="button"
            className="mt-1"
            disabled={!emailIsValid || partnerInviteSent || sendingPartnerInvite}
            spinner={sendingPartnerInvite}
            onClick={trySendInvite}
          >
            {partnerInviteSent ? "Invite sent" : "Send invite"}
          </Button>
        </div>
        <div className="mt-2 text-xs text-gray-500 text-center">
          We'll email them a link to join your team.
        </div>
      </div>
      <div className="mt-4 flex items-center rounded-md border border-orange-500 bg-orange-50 p-4 text-sm">
        <WarnIcon className="h-5 w-5 flex-shrink-0 stroke-orange-800" aria-hidden="true" />
        <div className="ml-3">No partner? Contact your tournament organizer to find one.</div>
      </div>
    </>
  );
};
